import { Injectable } from '@angular/core';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as qz from 'qz-tray';

import { HttpService } from './http.service';
import { UtilityService } from './utility.service';
import { PrintHtml, PrintTemplate } from '../models';
import { UserPrinterDocument } from '../../user-admin';
import { DocumentSize } from '../../app-shared/constants';
import { ShipperResponse } from 'tms-web-shared';
import { FileTypes } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  constructor(
    private httpService: HttpService,
    private utilityService: UtilityService,
  ) {}

  printTemplates(prints: PrintTemplate[]): Observable<any> {
    return this.utilityService.arrayExecuteAsyncSequentially(prints, (print: PrintTemplate) =>
      this.printTemplate(print.template, print.model, print.printer, print.copies),
    );
  }

  printHtmls(prints: PrintHtml[], reverse: boolean = false): Observable<any> {
    return this.utilityService.arrayExecuteAsyncSequentially(reverse ? prints.reverse() : prints, (print: PrintHtml) =>
      this.printHtml(print.html, print.printer, print.copies),
    );
  }

  printTemplate(template: string, model: any, printer: UserPrinterDocument, copies: number = 1): Observable<any> {
    return this.printFromTemplate(
      printer.documentSize === DocumentSize.small ? template : `${template}-medium`,
      model,
      printer.printerAddress,
      printer.documentWidth > 0 ? printer.documentWidth : undefined,
      printer.documentHeight > 0 ? printer.documentHeight : undefined,
      printer.documentOrientation,
      printer.topMargin,
      printer.rightMargin,
      printer.bottomMargin,
      printer.leftMargin,
      printer.documentUnits?.toLowerCase(),
      printer.printBarcode,
      printer.printQRCode,
      printer.documentSize,
      copies,
    );
  }

  printHtml(html: string, printer: UserPrinterDocument, copies: number = 1): Observable<any> {
    return this.printFromHtml(
      html,
      printer.printerAddress,
      printer.documentWidth > 0 ? printer.documentWidth : undefined,
      printer.documentHeight > 0 ? printer.documentHeight : undefined,
      printer.documentOrientation,
      printer.topMargin,
      printer.rightMargin,
      printer.bottomMargin,
      printer.leftMargin,
      printer.documentUnits?.toLowerCase(),
      copies,
    );
  }

  printShippingLabel(response: ShipperResponse): Observable<any> {
    qz.websocket
      .connect()
      .then(() => qz.printers.getDefault())
      .then((defaultPrinter: string) => {
        const config = qz.configs.create(defaultPrinter);
        const data = [
          {
            type: 'pixel',
            format: response.contentType === FileTypes.pdf ? 'pdf' : 'image',
            flavor: 'base64',
            data: response.label,
          },
        ];
        qz.print(config, data).then(
          () => qz.websocket.disconnect(),
          () => qz.websocket.disconnect(),
        );
      })
      .catch((e: any) => {
        alert(e);
        qz.websocket.disconnect();
      });

    return new Observable();
  }

  replaceTags(html: string, model: any): string {
    const tags = html.match(/<!--(.*)-->/g);
    if (tags) {
      for (const tag of tags) {
        const prop = tag.replace('<!--', '').replace('-->', '');
        html = html.replace(tag, this.utilityService.getNestedProperty(model, prop) ?? '');
      }
    }
    return html;
  }

  getPageModel(model: any, index: number = undefined, pages: any[] = undefined): string {
    return {
      ...model,
      page: `${index + 1} ${$localize`of`} ${pages.length}`,
    };
  }

  getPages<T>(items: T[], itemsPerPage: number = 8, itemsPerFooter: number = 3) {
    const pages = this.utilityService.chunk(items, itemsPerPage);

    // if last page doesn't have enough gap for a footer, push footer onto a new page
    if (pages[pages.length - 1].length + itemsPerFooter > itemsPerPage) {
      pages.push([]);
    }

    return pages;
  }

  private printFromTemplate(
    template: string,
    model: any,
    printer?: string,
    width = 6,
    height = 4,
    orientation = 'portrait',
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
    units = 'in',
    printBarcode = false,
    printQRCode = false,
    documentSize: string = DocumentSize.small,
    copies = 1,
  ): Observable<any> {
    return forkJoin([
      this.httpService.getHtmlTemplate('templates/base-template.html'),
      this.httpService.getHtmlTemplate(`templates/${template}.html`),
      this.httpService.getHtmlTemplate(
        documentSize === DocumentSize.small ? 'templates/barcode-label.html' : 'templates/barcode-label-medium.html',
      ),
      this.httpService.getHtmlTemplate(
        documentSize === DocumentSize.small ? 'templates/qrcode-label.html' : 'templates/qrcode-label-medium.html',
      ),
    ]).pipe(
      map(([base, template, barcode, qrcode]) => {
        template = this.replaceTags(template, model);
        template = this.replaceTags(base, { body: template });
        if (printBarcode && model.barcode?.length < 15) {
          const barcodeTemplate = this.replaceTags(barcode, model);
          template += barcodeTemplate;
        }
        if (printQRCode || model.barcode?.length > 14) {
          const qrcodeTemplate = this.replaceTags(qrcode, model);
          template += qrcodeTemplate;
        }
        return template;
      }),
      switchMap((html: string) =>
        this.printFromHtml(html, printer, width, height, orientation, top, right, bottom, left, units, copies),
      ),
    );
  }

  private printFromHtml(
    html: string,
    printer?: string,
    width = 6,
    height = 4,
    orientation = 'portrait',
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
    units = 'in',
    copies = 1,
  ): Observable<any> {
    return new Observable((observer: Subscriber<any>) => {
      qz.websocket
        .connect()
        .then(() => qz.printers.getDefault())
        .then((defaultPrinter: string) => {
          const config = qz.configs.create(printer ?? defaultPrinter, {
            copies: copies,
            units: units,
            size: {
              width,
              height,
            },
            orientation: orientation,
            margins: { top: top, right: right, bottom: bottom, left: left },
            scaleContent: false,
          });
          const data = [
            {
              type: 'pixel',
              format: 'html',
              flavor: 'plain',
              data: html,
            },
          ];
          qz.print(config, data).then(
            () => {
              qz.websocket.disconnect().then(() => {
                observer.next();
                observer.complete();
              });
            },
            () => {
              qz.websocket.disconnect().then(() => observer.complete());
            },
          );
        })
        .catch((e: any) => {
          alert(e);
          qz.websocket.disconnect().then(() => {
            observer.error(e);
            observer.complete();
          });
        });
    });
  }
}
